import { useId, useState, type KeyboardEvent, type Ref } from 'react';
import { cx } from '../outils';
import { Icone } from './Icone';
import { MessageDeChamp } from './MessageDeChamp';

export interface ChampTexteProps {
  libelle: string;
  type?: 'texte' | 'email' | 'mot-de-passe';
  valeur: string;
  onChange: (valeur: string) => void;
  onSortie?: () => void;
  exemple?: string;
  aide?: string;
  erreur?: string;
  /** Vrai : le champ est invalide sans message propre (message commun du formulaire). */
  invalide?: boolean;
  avertissement?: string;
  lien?: { libelle: string; onClick: () => void };
  facultatif?: boolean;
  disposition?: 'dessus' | 'ligne';
  taille?: 'normale' | 'compacte';
  desactive?: boolean;
  lectureSeule?: boolean;
  requis?: boolean;
  saisieAuto?: 'email' | 'current-password' | 'new-password' | 'off';
  nom?: string;
  longueurMax?: number;
  id?: string;
  /** Identifiants supplémentaires pour aria-describedby (message commun d'un formulaire). */
  decritPar?: string;
  autoFocus?: boolean;
  refSaisie?: Ref<HTMLInputElement>;
  onKeyDown?: (evenement: KeyboardEvent<HTMLInputElement>) => void;
}

/** Une ligne de texte, une adresse e-mail ou un mot de passe, avec libellé, aide et erreur. */
export function ChampTexte({
  libelle,
  type = 'texte',
  valeur,
  onChange,
  onSortie,
  exemple,
  aide,
  erreur,
  invalide,
  avertissement,
  lien,
  facultatif = false,
  disposition = 'dessus',
  taille = 'normale',
  desactive = false,
  lectureSeule = false,
  requis = false,
  saisieAuto,
  nom,
  longueurMax,
  id,
  decritPar,
  autoFocus,
  refSaisie,
  onKeyDown,
}: ChampTexteProps) {
  const genere = useId();
  const idSaisie = id ?? `champ-${genere}`;
  const [visible, setVisible] = useState(false);
  const [majuscules, setMajuscules] = useState(false);
  const idErreur = `${idSaisie}-erreur`;
  const idAide = `${idSaisie}-aide`;
  const idAvertissement = `${idSaisie}-avertissement`;
  const idMaj = `${idSaisie}-maj`;
  const motDePasse = type === 'mot-de-passe';
  const decrit = [
    erreur ? idErreur : null,
    decritPar ?? null,
    !erreur && aide ? idAide : null,
    avertissement ? idAvertissement : null,
    motDePasse && majuscules ? idMaj : null,
  ]
    .filter(Boolean)
    .join(' ');

  const libelleNoeud = (
    <label className="fc-champ-groupe__libelle" htmlFor={idSaisie}>
      {libelle}
      {facultatif && <span className="fc-champ-groupe__facultatif"> (facultatif)</span>}
    </label>
  );

  return (
    <div className={cx('fc-champ-groupe', disposition === 'ligne' && 'fc-champ-groupe--ligne')}>
      {lien ? (
        <div className="fc-champ-groupe__entete">
          {libelleNoeud}
          <a
            className="fc-champ-groupe__lien"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              lien.onClick();
            }}
          >
            {lien.libelle}
          </a>
        </div>
      ) : (
        libelleNoeud
      )}
      <div className={cx('fc-champ fc-champ-texte', taille === 'compacte' && 'fc-champ--compact')}>
        {type === 'email' && <Icone nom="mail" className="fc-champ__icone" />}
        {motDePasse && <Icone nom="lock" className="fc-champ__icone" />}
        <input
          ref={refSaisie}
          className="fc-champ__saisie"
          id={idSaisie}
          type={type === 'email' ? 'email' : motDePasse && !visible ? 'password' : 'text'}
          value={valeur}
          placeholder={exemple}
          autoComplete={saisieAuto}
          name={nom}
          maxLength={longueurMax}
          disabled={desactive}
          readOnly={lectureSeule}
          required={requis}
          autoFocus={autoFocus}
          spellCheck={type === 'texte' ? undefined : false}
          aria-invalid={erreur || invalide ? true : undefined}
          aria-describedby={decrit || undefined}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onSortie}
          onKeyDown={(e) => {
            if (motDePasse) setMajuscules(e.getModifierState('CapsLock'));
            onKeyDown?.(e);
          }}
          onKeyUp={(e) => motDePasse && setMajuscules(e.getModifierState('CapsLock'))}
        />
        {motDePasse && (
          <button
            type="button"
            className="fc-champ__bouton fc-champ-texte__oeil"
            aria-pressed={visible}
            aria-controls={idSaisie}
            aria-label="Afficher le mot de passe"
            title="Afficher le mot de passe"
            disabled={desactive}
            onClick={() => setVisible(!visible)}
          >
            <Icone nom="eye" className="fc-champ-texte__afficher" />
            <Icone nom="eye-off" className="fc-champ-texte__masquer" />
          </button>
        )}
      </div>
      {erreur ? (
        <MessageDeChamp id={idErreur} ton="erreur">
          {erreur}
        </MessageDeChamp>
      ) : (
        aide && <MessageDeChamp id={idAide}>{aide}</MessageDeChamp>
      )}
      {avertissement && (
        <MessageDeChamp id={idAvertissement} ton="avertissement">
          {avertissement}
        </MessageDeChamp>
      )}
      {motDePasse && majuscules && (
        <MessageDeChamp id={idMaj} ton="avertissement">
          Verrouillage des majuscules activé.
        </MessageDeChamp>
      )}
    </div>
  );
}
